/**
 * Seawater: its density near the surface, the pressure at a depth, and the
 * temperature a parcel would have brought to the surface. Enough to find the
 * mixed layer from a temperature and salinity profile (`waterColumn.ts`).
 *
 * The algorithms are UNESCO's (Fofonoff, N. P. & Millard, R. C. 1983,
 * *Algorithms for computation of fundamental properties of seawater*, UNESCO
 * Technical Papers in Marine Science 44), the international equation of state
 * of 1980 (EOS-80) and its companions, and each is checked in
 * `tests/seawater.test.ts` against the check values that paper prints. EOS-80
 * takes practical salinity, which the World Ocean Atlas gives, and
 * temperature on the 1968 scale (IPTS-68); the Atlas gives ITS-90, converted
 * by Saunders's factor (Saunders, P. 1990, *WOCE Newsletter* 10:
 * t68 = 1.00024 t90).
 */
import { DEG } from './math';

/** ITS-90 to IPTS-68 (Saunders 1990). */
const T68_PER_T90 = 1.00024;

/**
 * Density of seawater at one atmosphere, kg/m³, from practical salinity and
 * ITS-90 temperature, °C: EOS-80 (UNESCO 1981; Millero & Poisson 1981), the
 * pure-water density and its salinity terms.
 */
export function densityAtSurface(salinity: number, tempC90: number): number {
  const t = tempC90 * T68_PER_T90;
  const s = salinity;
  const water = 999.842594 + t * (6.793952e-2 + t * (-9.09529e-3 + t * (1.001685e-4 + t * (-1.120083e-6 + t * 6.536332e-9))));
  const a = 8.24493e-1 + t * (-4.0899e-3 + t * (7.6438e-5 + t * (-8.2467e-7 + t * 5.3875e-9)));
  const b = -5.72466e-3 + t * (1.0227e-4 - t * 1.6546e-6);
  const c = 4.8314e-4;
  return water + a * s + b * s * Math.sqrt(s) + c * s * s;
}

/**
 * Pressure, decibars, at a depth, m, and latitude (Saunders, P. M. 1981,
 * *Journal of Physical Oceanography* 11: 573): the depth-pressure relation of
 * a standard ocean, good to about 0.1% in the real one.
 */
export function pressureAtDepth(depthM: number, latDeg: number): number {
  const c1 = saundersC1(latDeg);
  return ((1 - c1) - Math.sqrt((1 - c1) ** 2 - 8.84e-6 * depthM)) / 4.42e-6;
}

/** Saunders's (1981) latitude term, c1, which both directions read (review M30: it was written in each). */
function saundersC1(latDeg: number): number {
  const s = Math.sin(latDeg * DEG);
  return (5.92 + 5.25 * s * s) * 1e-3;
}

/**
 * Depth, m, at a pressure, decibars, and latitude: Saunders's (1981) relation
 * solved the other way, z = (1 - c1) p - c2 p² with c2 = 2.21e-6, exactly
 * the inverse of \`pressureAtDepth\`. A cast is measured by pressure; the
 * Atlas and the game work in depth (M28, the hindcast).
 */
export function depthAtPressure(pressureDbar: number, latDeg: number): number {
  return (1 - saundersC1(latDeg)) * pressureDbar - 2.21e-6 * pressureDbar * pressureDbar;
}

/** The adiabatic lapse rate, °C per decibar, IPTS-68 (Bryden 1973, as UNESCO 1983 gives it). */
function lapseRate(s: number, t: number, p: number): number {
  const ds = s - 35;
  return (((-2.1687e-16 * t + 1.8676e-14) * t - 4.6206e-13) * p
    + ((2.7759e-12 * t - 1.1351e-10) * ds + ((-5.4481e-14 * t + 8.733e-12) * t - 6.7795e-10) * t + 1.8741e-8)) * p
    + (-4.2393e-8 * t + 1.8932e-6) * ds + ((6.6228e-10 * t - 6.836e-8) * t + 8.5258e-6) * t + 3.5803e-5;
}

/**
 * Potential temperature, ITS-90 °C: the temperature a parcel at pressure
 * `pressureDbar` would have brought adiabatically to `referenceDbar` (the
 * surface by default). Fofonoff's (1977) fourth-order Runge-Kutta
 * integration of Bryden's lapse rate, step for step as UNESCO 1983 prints it.
 */
export function potentialTemperature(salinity: number, tempC90: number, pressureDbar: number, referenceDbar = 0): number {
  let p = pressureDbar;
  let t = tempC90 * T68_PER_T90;
  const h = referenceDbar - p;
  let xk = h * lapseRate(salinity, t, p);
  t += 0.5 * xk;
  let q = xk;
  p += 0.5 * h;
  xk = h * lapseRate(salinity, t, p);
  t += 0.29289322 * (xk - q);
  q = 0.58578644 * xk + 0.121320344 * q;
  xk = h * lapseRate(salinity, t, p);
  t += 1.707106781 * (xk - q);
  q = 3.414213562 * xk - 4.121320344 * q;
  p += 0.5 * h;
  xk = h * lapseRate(salinity, t, p);
  return (t + (xk - 2 * q) / 6) / T68_PER_T90;
}

/** Potential density anomaly σθ, kg/m³: density at the surface of a parcel brought there, less 1,000. */
function sigmaTheta(salinity: number, tempC90: number, pressureDbar: number): number {
  return densityAtSurface(salinity, potentialTemperature(salinity, tempC90, pressureDbar)) - 1000;
}

/**
 * The criterion for the mixed layer in a climatological mean profile:
 * potential density 0.125 kg/m³ above its value at the surface (Monterey, G.
 * & Levitus, S. 1997, *Seasonal Variability of Mixed Layer Depth for the World
 * Ocean*, NOAA Atlas NESDIS 14), the one made for the World Ocean Atlas's
 * monthly profiles, from which that atlas found it (and the Atlas's cut
 * still does, M19; the game's mixed layer is de Boyer Montégut's since M32,
 * found by the single cast's criterion below). The finer 0.03 kg/m³ from
 * 10 m (de Boyer Montégut et al. 2004) is made for single casts; in a mean
 * profile, which blends casts mixed to different depths into a smooth
 * gradient, it stops at the shallowest of them (MEASURED in `KNOWN_ISSUES.md`
 * M19: 14 m in the North Atlantic's subpolar winter).
 */
export const MIXED_LAYER_SIGMA_STEP = 0.125;

/**
 * The depth of the mixed layer in a temperature and salinity profile at the
 * standard depths, m: where potential density first reaches its surface
 * value plus `MIXED_LAYER_SIGMA_STEP`, between the two levels either side by
 * a straight line. A column mixed to its last level (the seafloor, or the end
 * of the field) is mixed to that level's depth.
 */
export function mixedLayerDepth(depthsM: readonly number[], temperature: readonly number[], salinity: readonly number[], latDeg: number): number {
  const levels = Math.min(temperature.length, salinity.length);
  if (levels === 0) throw new Error('an empty profile has no mixed layer');
  const sigma = (k: number): number => sigmaTheta(salinity[k] as number, temperature[k] as number, pressureAtDepth(depthsM[k] as number, latDeg));
  const threshold = sigma(0) + MIXED_LAYER_SIGMA_STEP;
  let above = sigma(0);
  for (let k = 1; k < levels; k++) {
    const here = sigma(k);
    if (here >= threshold) {
      const d0 = depthsM[k - 1] as number;
      const d1 = depthsM[k] as number;
      return d0 + ((threshold - above) / (here - above)) * (d1 - d0);
    }
    above = here;
  }
  return depthsM[levels - 1] as number;
}

/**
 * The criterion for the mixed layer in a single cast, which a mean profile's
 * (`MIXED_LAYER_SIGMA_STEP`) stands in for: potential density 0.03 kg/m³
 * above its value at 10 m, a reference below the surface's daily warming (de
 * Boyer Montégut, C., Madec, G., Fischer, A. S., Lazar, A. & Iudicone, D.
 * 2004, *Journal of Geophysical Research* 109: C12003). The hindcast measures
 * HOT's casts by it (M29): by the mean profile's 0.125, a cast's mixed layer
 * reads 16.8 m deeper on average at Station ALOHA, deeper on 349 of 350
 * cruises and by as much as 86.2 m (MEASURED, M29), which M28 took for the
 * game's being too shallow.
 */
export const SINGLE_CAST_SIGMA_STEP = 0.03;
const SINGLE_CAST_REFERENCE_M = 10;

/**
 * The depth of the mixed layer in a single cast, m: below 10 m, where
 * potential density first reaches its value at 10 m (between the levels
 * either side) plus `SINGLE_CAST_SIGMA_STEP`, between the two levels either
 * side by a straight line. A cast mixed to its last level is mixed to that
 * level's depth; one that does not reach 10 m has no mixed layer read (NaN).
 */
export function singleCastMixedLayerDepth(depthsM: readonly number[], temperature: readonly number[], salinity: readonly number[], latDeg: number): number {
  const levels = Math.min(temperature.length, salinity.length);
  const sigma = (k: number): number => sigmaTheta(salinity[k] as number, temperature[k] as number, pressureAtDepth(depthsM[k] as number, latDeg));
  let k = 0;
  while (k < levels && (depthsM[k] as number) < SINGLE_CAST_REFERENCE_M) k++;
  if (k >= levels) return Number.NaN;
  // The density at 10 m, between the levels either side (or the first, if it is at or below 10 m).
  const below = sigma(k);
  const reference = k === 0 ? below
    : sigma(k - 1) + ((below - sigma(k - 1)) * (SINGLE_CAST_REFERENCE_M - (depthsM[k - 1] as number))) / ((depthsM[k] as number) - (depthsM[k - 1] as number));
  const threshold = reference + SINGLE_CAST_SIGMA_STEP;
  let d0 = k === 0 ? (depthsM[0] as number) : SINGLE_CAST_REFERENCE_M;
  let above = reference;
  for (; k < levels; k++) {
    const here = sigma(k);
    const d1 = depthsM[k] as number;
    if (here >= threshold && d1 > d0) return d0 + ((threshold - above) / (here - above)) * (d1 - d0);
    above = here;
    d0 = d1;
  }
  return depthsM[levels - 1] as number;
}
